import * as React from 'react'
import clsx from 'clsx'
import { useEventCallback } from '@applift/factor'
import { useGridApiContext } from '../hooks/utils/useGridApiContext'
import { useGridApiEventHandler } from '../hooks/utils/useGridApiEventHandler'
import { GridColumnHeaders } from './columnHeaders/GridColumnHeaders'
import { GridColumnHeadersInner } from './columnHeaders/GridColumnHeadersInner'
import { useGridColumnHeaders } from '../hooks/features/columnHeaders/useGridColumnHeaders'
import { useGridSelector } from '../hooks/utils/useGridSelector'
import { gridDensityHeaderHeightSelector } from '../hooks/features/density/densitySelector'
import { ReactTableHeaderGroup } from '../models/gridReactTable'
import { GridColumnHeaderRow } from './columnHeaders/GridColumnHeaderRow'
import { useGridRootProps } from '../hooks/utils/useGridRootProps'
import {
  GridColumnGroupHeader,
  HeaderGroupSide,
} from './columnHeaders/GridColumnGroupHeader'
import { GridColumnHeaderItem } from './columnHeaders/GridColumnHeaderItem'
import style from '../style'
import { ReactVirtualItem } from '../models/gridReactVirtual'
import { GridColumnHeaderSeparatorSides } from '../components/columnHeaders/GridColumnHeaderSeparator'
import { type GridColumnIdentifier } from '../hooks/features/focus'

interface DataGridColumnHeadersProps
  extends React.HTMLAttributes<HTMLDivElement> {
  innerRef?: React.Ref<HTMLDivElement>
  columnHeaderFocus: GridColumnIdentifier | null
  columnHeaderTabIndexState: GridColumnIdentifier | null
  hasOtherElementInTabSequence: boolean
}

export const DataGridColumnHeaders = React.forwardRef<
  HTMLDivElement,
  DataGridColumnHeadersProps
>(function GridColumnsHeader(props, ref) {
  const {
    innerRef,
    columnHeaderFocus,
    columnHeaderTabIndexState,
    hasOtherElementInTabSequence,
    ...other
  } = props

  const apiRef = useGridApiContext()
  const { showColumnGrouping, classes } = useGridRootProps()
  const [scrollbarSize, setScrollbarSize] = React.useState(0)
  const headerHeight = useGridSelector(apiRef, gridDensityHeaderHeightSelector)
  const {
    getRootProps,
    getInnerProps,
    renderConext,
    isDragging,
    dragCol,
    resizeCol,
  } = useGridColumnHeaders({
    innerRef,
  })

  const centerStartOffset =
    renderConext.tableInstance.getLeftVisibleLeafColumns().length

  const rightStartOffset =
    centerStartOffset +
    renderConext.tableInstance.getCenterVisibleLeafColumns().length

  const handleContentSizeChange = useEventCallback(() => {
    // Todo need to figure out why it was not updating rootDimensions
    setTimeout(() => {
      const rootDimensions = apiRef.current.getRootDimensions()
      if (!rootDimensions) {
        return
      }
      const newScrollbarSize = rootDimensions.hasScrollY
        ? rootDimensions.scrollBarSize
        : 0
      if (scrollbarSize !== newScrollbarSize) {
        setScrollbarSize(newScrollbarSize)
      }
    }, 0)
  })

  useGridApiEventHandler(
    apiRef,
    'virtualScrollerContentSizeChange',
    handleContentSizeChange
  )

  return (
    <GridColumnHeaders ref={ref} {...getRootProps(other)}>
      {renderConext.tableInstance.getLeftHeaderGroups()[0]?.headers.length ? (
        <div
          className={clsx(
            style.pinnedColumnHeaders,
            classes?.pinnedColumnHeaders,
            style['pinnedColumnHeaders--left'],
            classes?.['pinnedColumnHeaders--left']
          )}
        >
          {showColumnGrouping
            ? renderColumnGroupHeaders(
                renderConext.tableInstance.getLeftHeaderGroups(),
                headerHeight,
                'left',
                renderConext.showColumnRightBorder,
                renderConext.disableExtendRowFullWidth
              )
            : null}
          {renderColumnHeaders(
            renderConext.tableInstance.getLeftHeaderGroups(),
            headerHeight,
            renderConext.showColumnRightBorder,
            renderConext.disableExtendRowFullWidth,
            true,
            0,
            'left',
            columnHeaderFocus,
            columnHeaderTabIndexState,
            hasOtherElementInTabSequence
          )}
        </div>
      ) : null}
      <GridColumnHeadersInner isDragging={isDragging} {...getInnerProps()}>
        {showColumnGrouping
          ? renderColumnGroupHeaders(
              renderConext.tableInstance.getCenterHeaderGroups(),
              headerHeight,
              'center',
              renderConext.showColumnRightBorder,
              renderConext.disableExtendRowFullWidth
            )
          : null}
        {renderVirtualColumnHeaders(
          renderConext.tableInstance.getCenterHeaderGroups(),
          headerHeight,
          renderConext.showColumnRightBorder,
          renderConext.disableExtendRowFullWidth,
          false,
          centerStartOffset,
          resizeCol,
          dragCol,
          renderConext.renderContext?.colRange,
          columnHeaderFocus,
          columnHeaderTabIndexState,
          hasOtherElementInTabSequence
        )}
      </GridColumnHeadersInner>
      {renderConext.tableInstance.getRightHeaderGroups()[0]?.headers.length ? (
        <div
          className={clsx(
            style.pinnedColumnHeaders,
            classes?.pinnedColumnHeaders,
            style['pinnedColumnHeaders--right'],
            classes?.['pinnedColumnHeaders--right']
          )}
          style={
            renderConext.tableInstance.getRightHeaderGroups().length &&
            renderConext.tableInstance.getRightHeaderGroups()[0]?.headers.length
              ? { paddingRight: scrollbarSize }
              : undefined
          }
        >
          {showColumnGrouping
            ? renderColumnGroupHeaders(
                renderConext.tableInstance.getRightHeaderGroups(),
                headerHeight,
                'right',
                renderConext.showColumnRightBorder,
                renderConext.disableExtendRowFullWidth
              )
            : null}
          {renderColumnHeaders(
            renderConext.tableInstance.getRightHeaderGroups(),
            headerHeight,
            renderConext.showColumnRightBorder,
            renderConext.disableExtendRowFullWidth,
            true,
            rightStartOffset,
            'right',
            columnHeaderFocus,
            columnHeaderTabIndexState,
            hasOtherElementInTabSequence
          )}
        </div>
      ) : null}
    </GridColumnHeaders>
  )
})

const renderColumnGroupHeaders = (
  headerGroups: ReactTableHeaderGroup[],
  headerHeight: number,
  side: HeaderGroupSide,
  showColumnRightBorder: boolean,
  disableExtendRowFullWidth: boolean
) => {
  const onlyHeaderGroups = headerGroups.slice(0, headerGroups.length - 1)
  return onlyHeaderGroups.map(headerGroup => {
    return (
      <GridColumnHeaderRow
        style={{ height: headerHeight }}
        key={headerGroup.id}
      >
        {headerGroup.headers.map((header, index) => (
          <GridColumnGroupHeader
            key={header.id}
            pinnedSide={side}
            colIndex={index}
            cellHeight={headerHeight}
            extendRowFullWidth={disableExtendRowFullWidth}
            showColumnRightBorder={showColumnRightBorder}
            isLastColumn={index === headerGroup.headers.length - 1}
            maxDepth={onlyHeaderGroups.length}
            {...header}
          />
        ))}
      </GridColumnHeaderRow>
    )
  })
}

const renderColumnHeaders = (
  headerGroups: ReactTableHeaderGroup[],
  headerHeight: number,
  showColumnRightBorder: boolean,
  disableExtendRowFullWidth: boolean,
  disableColumnReorder: boolean,
  startOffset: number,
  side: HeaderGroupSide,
  columnHeaderFocus: GridColumnIdentifier | null,
  columnHeaderTabIndexState: GridColumnIdentifier | null,
  hasOtherElementInTabSequence: boolean
) => {
  const leafHeader = headerGroups[headerGroups.length - 1]!

  return (
    <GridColumnHeaderRow style={{ height: headerHeight }} key={leafHeader.id}>
      {leafHeader.headers.map((header, index) => {
        const colIndex = startOffset + index
        const hasFocus =
          columnHeaderFocus != null &&
          columnHeaderFocus.field === header.column.id
        const tabIndex =
          (columnHeaderTabIndexState != null &&
            columnHeaderTabIndexState.field === header.column.id) ||
          (colIndex === 0 && !hasOtherElementInTabSequence)
            ? 0
            : -1

        return (
          <GridColumnHeaderItem
            key={header.id}
            colIndex={colIndex}
            cellHeight={headerHeight}
            extendRowFullWidth={disableExtendRowFullWidth}
            showColumnRightBorder={showColumnRightBorder}
            isLastColumn={index === leafHeader.headers.length - 1}
            disableColumnReorder={disableColumnReorder}
            isDragging={false}
            isResizing={false}
            separatorSide={
              side === 'right'
                ? GridColumnHeaderSeparatorSides.Left
                : GridColumnHeaderSeparatorSides.Right
            }
            hasFocus={hasFocus}
            tabIndex={tabIndex}
            {...header}
          />
        )
      })}
    </GridColumnHeaderRow>
  )
}

const renderVirtualColumnHeaders = (
  headerGroups: ReactTableHeaderGroup[],
  headerHeight: number,
  showColumnRightBorder: boolean,
  disableExtendRowFullWidth: boolean,
  disableColumnReorder: boolean,
  startOffset: number,
  resizeCol: string,
  dragCol: string,
  colRange: ReactVirtualItem[] = [],
  columnHeaderFocus: GridColumnIdentifier | null,
  columnHeaderTabIndexState: GridColumnIdentifier | null,
  hasOtherElementInTabSequence: boolean
) => {
  const leafHeader = headerGroups[headerGroups.length - 1]!
  return (
    <GridColumnHeaderRow style={{ height: headerHeight }} key={leafHeader.id}>
      {colRange.map(one => {
        const header = leafHeader.headers[one.index]!
        if (!header) {
          return null
        }
        const colIndex = startOffset + one.index
        const hasFocus =
          columnHeaderFocus != null &&
          columnHeaderFocus.field === header.column.id
        const tabIndex =
          (columnHeaderTabIndexState != null &&
            columnHeaderTabIndexState.field === header.column.id) ||
          (colIndex === 0 && !hasOtherElementInTabSequence)
            ? 0
            : -1
        return (
          <GridColumnHeaderItem
            key={header.id}
            colIndex={colIndex}
            cellHeight={headerHeight}
            extendRowFullWidth={disableExtendRowFullWidth}
            showColumnRightBorder={showColumnRightBorder}
            isLastColumn={one.index === leafHeader.headers.length - 1}
            disableColumnReorder={disableColumnReorder}
            isResizing={header.id === resizeCol}
            isDragging={header.id === dragCol}
            tabIndex={tabIndex}
            hasFocus={hasFocus}
            {...header}
          />
        )
      })}
    </GridColumnHeaderRow>
  )
}
